import type { EventPersonBoolExp } from '@/gql/scalars';
import { getOrganizationMemberRole } from '@/screens/OrganizationMemberUpdate/OrganizationMemberUpdate.utils';
import formatMembershipRoleLabel from '@/utils/formatMembershipRoleLabel';

const ORGANIZATION_MEMBERS_SEARCH_MINIMUM = 1;

export type OrganizationMembersPerson = {
  event_id: string;
  person_id: string;
  role: string;
  person: {
    avatar_id?: string | null;
    badge_assignments: OrganizationMembersBadgeAssignment[];
    selected_badge_assignments?: OrganizationMembersSelectedBadgeAssignment[];
    color?: string | null;
    id: string;
    is_managed?: boolean | null;
    name?: string | null;
  };
};

export type OrganizationMembersBadgeAssignment = {
  id: string;
  preview_value?: string | null;
  badge: {
    color?: string | null;
    id: string;
    title?: string | null;
  };
};

export type OrganizationMemberBadgePreview = {
  color: string | null;
  id: string;
  title: string | null;
};

export type OrganizationMemberAttributeValue = {
  badge_attribute_id: string;
  formatted_value?: string | null;
  value?: string | null;
};

export type OrganizationMembersSelectedBadgeAssignment = {
  badge_assignee_attributes: OrganizationMemberAttributeValue[];
  id: string;
};

export type OrganizationMembersBadgeChoice = {
  badge_assignees_aggregate: {
    aggregate?: { count?: number | null } | null;
  };
  color?: string | null;
  id: string;
  title?: string | null;
};

export type OrganizationMemberBadgeChoiceRow = {
  assignmentCount: number;
  color: string | null;
  id: string;
  title: string;
};

export type OrganizationMemberRow = {
  attributeValues: Record<string, string | null>;
  roleLabel: string | null;
  isManaged: boolean;
  badges: string;
  badgePreviews: OrganizationMemberBadgePreview[];
  id: string;
  personAvatarId: string | null;
  personColor: string | null;
  personId: string;
  personName: string;
  selected: string | null;
};

export function toOrganizationMemberRow(
  member: OrganizationMembersPerson,
  organizationOwnerId?: string | null,
): OrganizationMemberRow {
  const badgePreviews = member.person.badge_assignments.map((assignment) => ({
    color: assignment.badge.color ?? null,
    id: assignment.id,
    title: assignment.preview_value ?? assignment.badge.title ?? null,
  }));

  return {
    attributeValues: getOrganizationMemberAttributeValues(
      member.person.selected_badge_assignments?.[0]
        ?.badge_assignee_attributes ?? [],
    ),
    roleLabel: formatMembershipRoleLabel({
      isManaged: member.person.is_managed,
      role: getOrganizationMemberRole({
        assignedRole: member.role,
        organizationOwnerId,
        personId: member.person_id,
      }),
    }),
    badges: formatOrganizationMemberLabels(
      member.person.badge_assignments.map(
        (assignment) => assignment.preview_value ?? assignment.badge.title,
      ),
    ),
    badgePreviews,
    id: `${member.event_id}:${member.person_id}`,
    isManaged: Boolean(member.person.is_managed),
    personId: member.person_id,
    personName: member.person.name ?? 'Unknown Member',
    personAvatarId: member.person.avatar_id ?? null,
    personColor: member.person.color ?? null,
    selected: null,
  };
}

export function getOrganizationMemberAttributeColumnId(attributeId: string) {
  return `attribute_${attributeId}`;
}

export function getOrganizationMemberAttributeValues(
  attributes: OrganizationMemberAttributeValue[],
) {
  const attributeValues: Record<string, string | null> = {};

  for (const attribute of attributes) {
    attributeValues[
      getOrganizationMemberAttributeColumnId(attribute.badge_attribute_id)
    ] = attribute.formatted_value ?? attribute.value ?? null;
  }

  return attributeValues;
}

export function toOrganizationMemberBadgeChoiceRow(
  badge: OrganizationMembersBadgeChoice,
): OrganizationMemberBadgeChoiceRow {
  return {
    id: badge.id,
    title: badge.title ?? 'Untitled Badge',
    color: badge.color ?? null,
    assignmentCount: badge.badge_assignees_aggregate.aggregate?.count ?? 0,
  };
}

export function getOrganizationMembersWhere({
  badgeIds,
  organizationId,
  searchTerm,
}: {
  badgeIds: string[];
  organizationId: string;
  searchTerm: string;
}): EventPersonBoolExp {
  const filters: EventPersonBoolExp[] = [
    {
      event_id: {
        _eq: organizationId,
      },
    },
  ];

  if (badgeIds.length) {
    filters.push({
      person: {
        badge_assignments: {
          badge_id: { _in: badgeIds },
          event_id: { _eq: organizationId },
        },
      },
    });
  }

  if (searchTerm.length >= ORGANIZATION_MEMBERS_SEARCH_MINIMUM) {
    filters.push({ person: { name: { _ilike: `%${searchTerm}%` } } });
  }

  return { _and: filters };
}

function formatOrganizationMemberLabels(labels: (string | null | undefined)[]) {
  const populatedLabels = labels.filter((label) => Boolean(label?.trim()));

  return populatedLabels.length ? populatedLabels.join(', ') : '-';
}
